/**
 * zod helpers shared by the contract's own schemas and the SpecTree API
 * (through `@spectree/shared`), defined once here, the lowest package (epic
 * PI-SPEC-SW-E295):
 *
 * - `boundedRecord`: a `z.record` with an entry cap (GHSA-mrhx-c7xc-m3w5).
 * - `isoDateTime`: an ISO 8601 date-time string accepting exactly what zod 3
 *   accepted, so the move to zod 4 changes no accepted input.
 */
import { z } from "zod";
/**
 * Marker set on the inner `z.record` built by {@link boundedRecord}, holding
 * its entry cap. The SpecTree API's request-schema bounds test reads it to
 * tell a bounded record from a bare `z.record`. Production code never reads it.
 */
export const BOUNDED_RECORD = Symbol.for("spectree.boundedRecord");
/**
 * A `z.record` that rejects objects with more than `maxEntries` keys before any
 * key or value is parsed.
 *
 * zod 4 has no `.max()` on records, so a bare `z.record` parses every entry of
 * an oversized object and can collect one issue per failing entry. Here a
 * cheap key-count check runs first; when it fails the pipe stops with exactly
 * one issue and the record is never walked.
 *
 * `message` replaces the default issue message for an oversized object. A
 * value that is not an object fails on the record with zod's type error.
 */
export function boundedRecord(key, value, maxEntries, message = `Expected an object with at most ${String(maxEntries)} entries`) {
    const record = z.record(key, value);
    Object.defineProperty(record, BOUNDED_RECORD, { value: maxEntries, enumerable: false });
    return z
        .custom(
    // Only objects are counted; anything else goes on to the record, which
    // reports the type error.
    (v) => v === null || typeof v !== "object" || Array.isArray(v) || Object.keys(v).length <= maxEntries, { error: message })
        .pipe(record);
}
/** zod 3.25's date part: calendar-valid dates, leap years included. */
const ISO_DATE = String.raw `((\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-((0[13578]|1[02])-(0[1-9]|[12]\d|3[01])|(0[469]|11)-(0[1-9]|[12]\d|30)|(02)-(0[1-9]|1\d|2[0-8])))`;
/** zod 3.25's time part with precision unset: seconds and fractions optional. */
const ISO_TIME = String.raw `([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?`;
const ISO_DATE_TIME_UTC = new RegExp(`^${ISO_DATE}T${ISO_TIME}(Z)$`);
const ISO_DATE_TIME_OFFSET = new RegExp(String.raw `^${ISO_DATE}T${ISO_TIME}(Z|([+-]\d{2}:?\d{2}))$`);
/**
 * An ISO 8601 date-time string, matched with zod 3.25's `datetime()` regex.
 *
 * zod 4's `z.iso.datetime()` requires seconds and a colon in offsets, so it
 * rejects `2026-10-23T10:00Z` and `+0530`, which zod 3 accepted. With
 * `offset: true` an offset (`+05:30`, `+0530`) is allowed as well as `Z`;
 * without it only `Z`.
 */
export function isoDateTime(options = {}) {
    return z.string().regex(options.offset ? ISO_DATE_TIME_OFFSET : ISO_DATE_TIME_UTC, { error: "Invalid ISO datetime" });
}
//# sourceMappingURL=zod-helpers.js.map